import { restoreErrorMessage } from "@/api/archived";
import {
  useConnectors,
  useDiscoverConnector,
  useOrgConnectors,
  useSaveConnector,
} from "@/api/connectors";
import { isApiError } from "@/api/utils";
import {
  LcBadge,
  NESTED_DIALOG_Z,
  paneFieldInputClassName,
  SettingRow,
} from "@/components/AdminPanel/Organization/components/manage-pane-primitives";
import { EYEBROW, HOVER_T } from "@/components/rail-kit/registers";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import {
  Connector,
  ConnectorAuthInject,
  ConnectorDiscovery,
  ConnectorDiscoveryExisting,
  ConnectorField,
  ConnectorOAuthRegistration,
  ConnectorPayload,
  ConnectorStatus,
  ManagedConnectorAuthMethod,
  OrgConnector,
} from "@cohesive/api-client";
import { IntegrationIcon } from "@cohesive/ui-components";
import { ChevronRight, Info, Plus, X } from "lucide-react";
import React, { useEffect, useId, useState } from "react";
import toast from "react-hot-toast";

type AuthMethod = ManagedConnectorAuthMethod;
type Owner = "organization" | "user";

/** What the dialog edits: an organization connector, or a user's own from their connectors list. */
type EditableConnector = Connector | OrgConnector;

type Choice<T> = { value: T; title: string; tag?: string; desc: string };

function authChoices(owner: Owner): Choice<AuthMethod>[] {
  const members = owner === "organization";
  return [
    {
      value: "oauth",
      title: "Sign in",
      tag: "OAuth",
      desc: members
        ? "Each member signs in to the server with their own account before using it."
        : "You sign in to the server with your own account before using it.",
    },
    {
      value: "secret",
      title: "API key",
      desc: members
        ? "Each member pastes their own key, stored encrypted."
        : "You paste a key, stored encrypted.",
    },
    {
      value: "none",
      title: "No sign-in",
      desc: "For open servers that need no account.",
    },
  ];
}

export const REGISTRATIONS: (Choice<ConnectorOAuthRegistration> & {
  label: string;
})[] = [
  {
    value: "cimd",
    label: "CIMD",
    title: "Use Cohesive's published identity",
    tag: "CIMD",
    desc: "Nothing to set up. The server has to accept client metadata documents.",
  },
  {
    value: "dcr",
    label: "DCR",
    title: "Register automatically",
    tag: "DCR",
    desc: "The server registers a client for each connection.",
  },
  {
    value: "static",
    label: "Static",
    title: "Use your own OAuth client",
    desc: "Sign-ins go through an OAuth app you registered with the provider.",
  },
];

export const AUTH_LABELS: Record<AuthMethod, string> = {
  oauth: "OAuth",
  secret: "Secret",
  none: "No auth",
};

/** The auth a connector may be given, in the order the dialog offers it. */
type AuthOptions = {
  methods: AuthMethod[];
  registrations: ConnectorOAuthRegistration[];
};

/**
 * What a discovered server takes: no auth or a secret when it didn't ask to sign in, OAuth with
 * the registrations its metadata supports, or a hand-entered static client without metadata. An
 * edit has no discovery and offers everything.
 */
function authOptions(discovery?: ConnectorDiscovery): AuthOptions {
  if (!discovery) {
    return {
      methods: ["oauth", "secret", "none"],
      registrations: ["cimd", "dcr", "static"],
    };
  }
  if (!discovery.requires_auth) {
    return { methods: ["secret", "none"], registrations: [] };
  }
  const oauth = discovery.oauth;
  if (!oauth) {
    return { methods: ["oauth", "secret", "none"], registrations: ["static"] };
  }
  const registrations: ConnectorOAuthRegistration[] = [];
  if (oauth.cimd) registrations.push("cimd");
  if (oauth.dcr) registrations.push("dcr");
  registrations.push("static");
  return { methods: ["oauth", "secret"], registrations };
}

/**
 * The scopes a new connector starts with, in the MCP spec's order: what the server's 401 asked for,
 * else everything its authorization server lists. Admins untick what they don't want.
 */
function defaultScopes(discovery: ConnectorDiscovery): string[] {
  const oauth = discovery.oauth;
  if (oauth?.challenge_scopes?.length) return oauth.challenge_scopes;
  return oauth?.scopes_supported ?? [];
}

/**
 * The scopes offered as checkboxes: what the server lists or asked for, then any saved scope it no
 * longer lists, so a saved choice stays visible. Empty when the server names none.
 */
function offeredScopes(
  discovery: ConnectorDiscovery | undefined,
  saved: string[] = [],
): string[] {
  const listed = [
    ...(discovery?.oauth?.scopes_supported ?? []),
    ...(discovery?.oauth?.challenge_scopes ?? []),
  ];
  if (!listed.length) return [];
  return [...new Set([...listed, ...saved])];
}

function discoveredAuthMethod(discovery: ConnectorDiscovery): AuthMethod {
  return discovery.requires_auth ? "oauth" : "none";
}

/** The registration the server's metadata offers first. A static client is never detected. */
function discoveredRegistration(
  discovery: ConnectorDiscovery,
): ConnectorOAuthRegistration | undefined {
  if (discovery.oauth?.cimd) return "cimd";
  if (discovery.oauth?.dcr) return "dcr";
  return undefined;
}

/** A slug as the API accepts it: lowercase words of letters and digits, hyphen-separated. */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64)
    .replace(/-+$/, "");
}

type Form = {
  display_name: string;
  slug: string;
  description: string;
  image_url: string;
  url: string;
  auth_method: AuthMethod;
  oauth_registration: ConnectorOAuthRegistration;
  oauth_scopes: string;
  oauth_issuer: string;
  authorization_endpoint: string;
  token_endpoint: string;
  oauth_client_id: string;
  oauth_client_secret: string;
  fields: ConnectorField[];
  inject: ConnectorAuthInject[];
  status: Exclude<ConnectorStatus, "deprecated">;
};

const DEFAULT_FIELDS: ConnectorField[] = [
  { name: "api_key", label: "API key" },
];
const DEFAULT_INJECT: ConnectorAuthInject[] = [
  { in: "header", name: "Authorization", value: "Bearer {api_key}" },
];

function initialForm(connector?: EditableConnector): Form {
  const fields = connector?.auth_config?.fields;
  const inject = connector?.auth_config?.inject;
  return {
    display_name: connector?.display_name ?? "",
    slug: connector?.slug ?? "",
    description: connector?.description ?? "",
    image_url: connector?.image_url ?? "",
    url: connector?.url ?? "",
    auth_method:
      connector && connector.auth_method !== "pipedream"
        ? connector.auth_method
        : "oauth",
    oauth_registration: connector?.oauth_registration ?? "cimd",
    oauth_scopes: connector?.oauth_scopes?.join(" ") ?? "",
    oauth_issuer: connector?.oauth_issuer ?? "",
    authorization_endpoint: connector?.authorization_endpoint ?? "",
    token_endpoint: connector?.token_endpoint ?? "",
    oauth_client_id: connector?.oauth_client_id ?? "",
    oauth_client_secret: "",
    fields: fields?.length ? fields : DEFAULT_FIELDS,
    inject: inject?.length ? inject : DEFAULT_INJECT,
    // Saving takes only enabled or disabled; a deprecated connector saves as enabled.
    status: connector?.status === "disabled" ? "disabled" : "enabled",
  };
}

function discoveredForm(url: string, discovery: ConnectorDiscovery): Form {
  return {
    ...initialForm(),
    display_name: discovery.display_name,
    slug: discovery.slug,
    description: discovery.description ?? "",
    image_url: discovery.image_url ?? "",
    url,
    auth_method: discoveredAuthMethod(discovery),
    oauth_registration: discoveredRegistration(discovery) ?? "static",
    oauth_scopes: defaultScopes(discovery).join(" "),
    oauth_issuer: discovery.oauth?.issuer ?? "",
    authorization_endpoint: discovery.oauth?.authorization_endpoint ?? "",
    token_endpoint: discovery.oauth?.token_endpoint ?? "",
  };
}

type SlugHint = { error?: string; note?: string };

/**
 * What a slug would collide with among the connectors already loaded: another of the owner's
 * own, which the save refuses, or a shared one it overrides. The server's checks on save stay
 * authoritative.
 */
export function slugHint(
  owner: Owner,
  slug: string,
  editingId: string | undefined,
  connectors: Connector[] = [],
  orgConnectors: OrgConnector[] = [],
): SlugHint {
  slug = slug.trim();
  if (!slug) return {};
  const match = (type: string) =>
    connectors.find(
      (c) => c.type === type && c.slug === slug && c.id !== editingId,
    );
  const own =
    owner === "organization"
      ? orgConnectors.find((c) => c.slug === slug && c.id !== editingId)
      : match("organization-user");
  if (own)
    return { error: `Already used by your ${own.display_name} connector.` };
  if (owner === "user") {
    const org = match("organization");
    if (org)
      return { note: `Overrides your organization's ${org.display_name}.` };
  }
  const global = match("global");
  if (!global) return {};
  if (owner === "organization") {
    return {
      note: `Overrides the global ${global.display_name}. Members connected to it will be disconnected.`,
    };
  }
  return global.connection
    ? {
        note: `Overrides the global ${global.display_name}. You'll be disconnected from it.`,
      }
    : {};
}

/** The shared connector a save would override while connections to it are live. */
type Replacing = { name: string; members?: number };

function replacingMessage(owner: Owner, replacing: Replacing): string {
  if (owner === "user") {
    return `This replaces your ${replacing.name} connection and disconnects it. Continue?`;
  }
  const members = replacing.members ?? 0;
  return `${members} ${members === 1 ? "member is" : "members are"} connected to the global ${replacing.name}. Saving disconnects them; they'll need to connect to yours.`;
}

/**
 * The definition the API takes, carrying only what the auth method accepts: it refuses OAuth
 * settings on any other method, and a pinned server on anything but a static client. Inputs and
 * extra authorization params have no fields here, so an edit passes them through.
 */
function toPayload(
  owner: Owner,
  form: Form,
  connector?: EditableConnector,
): ConnectorPayload {
  const payload: ConnectorPayload = {
    display_name: form.display_name.trim(),
    slug: form.slug.trim(),
    description: form.description.trim(),
    image_url: form.image_url.trim(),
    url: form.url.trim(),
    inputs_schema: connector?.inputs_schema ?? [],
    auth_method: form.auth_method,
  };
  if (owner === "organization") payload.status = form.status;
  if (form.auth_method === "secret") {
    payload.auth_config = {
      fields: form.fields.map((f) => ({
        name: f.name.trim(),
        label: f.label.trim(),
      })),
      inject: form.inject.map((i) => ({
        in: i.in,
        name: i.name.trim(),
        value: i.value.trim(),
      })),
    };
  }
  if (form.auth_method !== "oauth") return payload;

  payload.oauth_registration = form.oauth_registration;
  const scopes = form.oauth_scopes.split(/[\s,]+/).filter(Boolean);
  if (scopes.length) payload.oauth_scopes = scopes;
  if (connector?.extra_authorization_params) {
    payload.extra_authorization_params = connector.extra_authorization_params;
  }
  if (form.oauth_registration === "static") {
    payload.oauth_issuer = form.oauth_issuer.trim();
    payload.authorization_endpoint = form.authorization_endpoint.trim();
    payload.token_endpoint = form.token_endpoint.trim();
    payload.oauth_client_id = form.oauth_client_id.trim();
    // Omitted, the server keeps the stored secret of the same client.
    const secret = form.oauth_client_secret.trim();
    if (secret) payload.oauth_client_secret = secret;
  }
  return payload;
}

function isComplete(form: Form): boolean {
  const filled = (...values: string[]) => values.every((v) => v.trim());
  if (!filled(form.display_name, form.slug, form.url)) return false;
  if (form.auth_method === "secret") {
    return (
      form.fields.length > 0 &&
      form.inject.length > 0 &&
      form.fields.every((f) => filled(f.name, f.label)) &&
      form.inject.every((i) => filled(i.name, i.value))
    );
  }
  // A static client without a secret is a public one.
  if (form.auth_method === "oauth" && form.oauth_registration === "static") {
    return filled(
      form.oauth_issuer,
      form.authorization_endpoint,
      form.token_endpoint,
      form.oauth_client_id,
    );
  }
  return true;
}

function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="inline-flex w-fit flex-wrap items-center gap-1 rounded-md border border-input bg-background p-1"
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          onClick={() => onChange(option.value)}
          className={cn(
            "cursor-pointer rounded-sm px-3 py-1.5 text-sm",
            HOVER_T,
            value === option.value
              ? "bg-n-10 text-n-95 dark:bg-n-20"
              : "text-n-60 hover:text-n-90",
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function RadioCards<T extends string>({
  label,
  value,
  options,
  detected,
  onChange,
}: {
  label: string;
  value: T;
  options: Choice<T>[];
  detected?: T;
  onChange: (value: T) => void;
}) {
  const id = useId();
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-col gap-2">
      {options.map((option) => {
        const checked = value === option.value;
        const descId = `${id}-${option.value}`;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={checked}
            aria-label={
              option.tag ? `${option.title} (${option.tag})` : option.title
            }
            aria-describedby={descId}
            onClick={() => onChange(option.value)}
            className={cn(
              "flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2.5 text-left",
              HOVER_T,
              checked
                ? "border-n-40 bg-n-05"
                : "border-n-10 hover:border-n-20 hover:bg-n-05",
            )}
          >
            <span
              aria-hidden
              className={cn(
                "mt-1 inline-flex size-3.5 shrink-0 items-center justify-center rounded-full border",
                checked ? "border-n-90" : "border-n-30",
              )}
            >
              {checked && <span className="size-1.5 rounded-full bg-n-90" />}
            </span>
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="flex flex-wrap items-center gap-2 text-sm font-medium text-n-90">
                {option.title}
                {option.tag && (
                  <span className="font-mono text-2xs font-normal text-n-50">
                    {option.tag}
                  </span>
                )}
                {detected === option.value && (
                  <LcBadge tone="active">Detected</LcBadge>
                )}
              </span>
              <span id={descId} className="text-sm text-n-60">
                {option.desc}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function InfoTip({ label, children }: { label: string; children: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className="inline-flex text-n-40 hover:text-n-70"
        >
          <Info className="size-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className={cn("max-w-72 text-left", NESTED_DIALOG_Z)}
      >
        {children}
      </TooltipContent>
    </Tooltip>
  );
}

function Disclosure({
  label,
  summary,
  defaultOpen,
  info,
  children,
}: {
  label: string;
  /** What's inside at a glance, shown beside the label. */
  summary?: string;
  defaultOpen?: boolean;
  info?: string;
  children: React.ReactNode;
}) {
  return (
    <Collapsible defaultOpen={defaultOpen} className="flex flex-col gap-4">
      <div className="flex items-center gap-1.5">
        <CollapsibleTrigger
          className={cn(
            "group flex cursor-pointer items-center gap-1 text-sm text-n-60 hover:text-n-90",
            HOVER_T,
          )}
        >
          <ChevronRight className="size-4 transition-transform group-data-[state=open]:rotate-90" />
          {label}
        </CollapsibleTrigger>
        {info && (
          <InfoTip label={`About ${label.toLowerCase()}`}>{info}</InfoTip>
        )}
        {summary && (
          <span className="min-w-0 truncate font-mono text-xs text-n-50">
            {summary}
          </span>
        )}
      </div>
      <CollapsibleContent className="flex flex-col gap-4">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

function Field({
  label,
  optional,
  hint,
  children,
}: {
  label: string;
  optional?: boolean;
  hint?: React.ReactNode;
  children: (id: string) => React.ReactNode;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline gap-2">
        <Label htmlFor={id}>{label}</Label>
        {optional && <span className="text-xs text-n-50">Optional</span>}
      </div>
      {children(id)}
      {hint && <p className="text-sm text-n-50">{hint}</p>}
    </div>
  );
}

function TextField({
  label,
  optional,
  hint,
  value,
  onChange,
  placeholder,
  type,
}: {
  label: string;
  optional?: boolean;
  hint?: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <Field label={label} optional={optional} hint={hint}>
      {(id) => (
        <Input
          id={id}
          type={type}
          autoComplete="off"
          spellCheck={false}
          placeholder={placeholder}
          className={paneFieldInputClassName}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </Field>
  );
}

function Group({
  head,
  info,
  children,
}: {
  head: string;
  info?: string;
  children: React.ReactNode;
}) {
  return (
    <fieldset className="flex flex-col gap-4">
      <legend className="flex items-center gap-1.5 pb-3">
        <span className={EYEBROW}>{head}</span>
        {info && (
          <InfoTip label={`About ${head.toLowerCase()}`}>{info}</InfoTip>
        )}
      </legend>
      {children}
    </fieldset>
  );
}

function RemoveButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      aria-label={label}
      className="shrink-0 text-n-50 hover:text-n-90"
      onClick={onClick}
    >
      <X />
    </Button>
  );
}

function AddButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="self-start text-n-60 hover:text-n-90"
      onClick={onClick}
    >
      <Plus />
      {children}
    </Button>
  );
}

function SecretSettings({
  form,
  set,
}: {
  form: Form;
  set: (patch: Partial<Form>) => void;
}) {
  const setField = (index: number, patch: Partial<ConnectorField>) =>
    set({
      fields: form.fields.map((f, i) => (i === index ? { ...f, ...patch } : f)),
    });
  const setInject = (index: number, patch: Partial<ConnectorAuthInject>) =>
    set({
      inject: form.inject.map((f, i) => (i === index ? { ...f, ...patch } : f)),
    });

  return (
    <>
      <div className="flex flex-col gap-2">
        <p className="text-sm text-n-60">
          What's entered to connect. Stored encrypted, never shown again.
        </p>
        {form.fields.map((field, index) => (
          <div key={index} className="flex items-end gap-2">
            <div className="grid min-w-0 flex-1 grid-cols-2 gap-2">
              <TextField
                label="Field name"
                placeholder="api_key"
                value={field.name}
                onChange={(name) => setField(index, { name })}
              />
              <TextField
                label="Field label"
                placeholder="API key"
                value={field.label}
                onChange={(label) => setField(index, { label })}
              />
            </div>
            <RemoveButton
              label={`Remove field ${field.name || index + 1}`}
              onClick={() =>
                set({ fields: form.fields.filter((_, i) => i !== index) })
              }
            />
          </div>
        ))}
        <AddButton
          onClick={() =>
            set({ fields: [...form.fields, { name: "", label: "" }] })
          }
        >
          Add field
        </AddButton>
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-sm text-n-60">
          Where the fields go on each request. Name a field in braces, like{" "}
          <code className="font-mono text-xs">Bearer {"{api_key}"}</code>.
        </p>
        {form.inject.map((inject, index) => (
          <div
            key={index}
            className="flex flex-col gap-2 rounded-md border border-n-10 p-3"
          >
            <div className="flex items-center justify-between gap-2">
              <Segmented
                label={`Inject ${index + 1} location`}
                value={inject.in}
                options={[
                  { value: "header", label: "Header" },
                  { value: "query", label: "Query" },
                ]}
                onChange={(value) => setInject(index, { in: value })}
              />
              <RemoveButton
                label={`Remove inject ${inject.name || index + 1}`}
                onClick={() =>
                  set({ inject: form.inject.filter((_, i) => i !== index) })
                }
              />
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <TextField
                label={
                  inject.in === "header" ? "Header name" : "Parameter name"
                }
                placeholder={inject.in === "header" ? "Authorization" : "key"}
                value={inject.name}
                onChange={(name) => setInject(index, { name })}
              />
              <TextField
                label="Value"
                placeholder="{api_key}"
                value={inject.value}
                onChange={(value) => setInject(index, { value })}
              />
            </div>
          </div>
        ))}
        <AddButton
          onClick={() =>
            set({
              inject: [
                ...form.inject,
                {
                  in: "header",
                  name: "",
                  value: form.fields[0] ? `{${form.fields[0].name}}` : "",
                },
              ],
            })
          }
        >
          Add inject
        </AddButton>
      </div>
    </>
  );
}

function scopesHint(owner: Owner): string {
  return owner === "organization"
    ? "Members are asked to grant these when they sign in."
    : "You're asked to grant these when you sign in.";
}

function ScopeChoices({
  owner,
  scopes,
  value,
  onChange,
}: {
  owner: Owner;
  scopes: string[];
  value: string;
  onChange: (value: string) => void;
}) {
  const id = useId();
  const chosen = value.split(/[\s,]+/).filter(Boolean);
  const toggle = (scope: string, checked: boolean) =>
    onChange(
      scopes
        .filter((s) => (s === scope ? checked : chosen.includes(s)))
        .join(" "),
    );
  return (
    <div className="flex flex-col gap-1.5">
      <Label id={id}>Scopes</Label>
      <div
        role="group"
        aria-labelledby={id}
        className="flex flex-wrap gap-x-4 gap-y-2"
      >
        {scopes.map((scope) => (
          <label
            key={scope}
            className="flex cursor-pointer items-center gap-2 text-sm text-n-80"
          >
            <Checkbox
              checked={chosen.includes(scope)}
              onCheckedChange={(checked) => toggle(scope, checked === true)}
            />
            <code className="font-mono text-xs">{scope}</code>
          </label>
        ))}
      </div>
      <p className="text-sm text-n-50">{scopesHint(owner)}</p>
    </div>
  );
}

function OAuthSettings({
  owner,
  form,
  set,
  hasSecret,
  dropsSecret,
  registrations,
  detected,
  scopesOffered,
  scopesLoading,
}: {
  owner: Owner;
  form: Form;
  set: (patch: Partial<Form>) => void;
  hasSecret: boolean;
  dropsSecret: boolean;
  registrations: ConnectorOAuthRegistration[];
  detected?: ConnectorOAuthRegistration;
  scopesOffered: string[];
  /** An edit is still asking the server which scopes it offers. */
  scopesLoading: boolean;
}) {
  const scopes = scopesLoading ? (
    <p className="text-sm text-n-50">Checking the server for its scopes…</p>
  ) : scopesOffered.length ? (
    <ScopeChoices
      owner={owner}
      scopes={scopesOffered}
      value={form.oauth_scopes}
      onChange={(oauth_scopes) => set({ oauth_scopes })}
    />
  ) : (
    <TextField
      label="Scopes"
      placeholder="read write"
      hint={`Space-separated. ${scopesHint(owner)}`}
      value={form.oauth_scopes}
      onChange={(oauth_scopes) => set({ oauth_scopes })}
    />
  );
  const chosen = form.oauth_scopes.split(/[\s,]+/).filter(Boolean);
  // Folded, the scopes show as a summary, so the server's answer landing never moves the form.
  const summary = chosen.length ? chosen.join(", ") : "None";
  // Prefilled endpoints stay out of the way. Without metadata they're the admin's to enter.
  const endpointsMissing = ![
    form.oauth_issuer,
    form.authorization_endpoint,
    form.token_endpoint,
  ].every((v) => v.trim());
  return (
    <Group
      head="OAuth client"
      info="Who Cohesive signs in as on the server's authorization server. The first two need nothing from you. Your own client uses an OAuth app you registered with the provider."
    >
      <RadioCards
        label="OAuth client"
        value={form.oauth_registration}
        options={REGISTRATIONS.filter((r) => registrations.includes(r.value))}
        detected={detected}
        onChange={(oauth_registration) => set({ oauth_registration })}
      />
      {form.oauth_registration === "static" ? (
        <>
          <TextField
            label="Client ID"
            hint="The public identifier from your provider's OAuth app settings, not an API key or token."
            value={form.oauth_client_id}
            onChange={(oauth_client_id) => set({ oauth_client_id })}
          />
          <TextField
            label="Client secret"
            optional
            type="password"
            placeholder={hasSecret ? "Leave blank to keep" : undefined}
            hint={
              hasSecret ? (
                "A secret is stored. Leave blank to keep it."
              ) : dropsSecret ? (
                <span className="text-lc-warning">
                  The stored secret will be removed because the client or
                  authorization server changed. Enter it again if your
                  authorization server requires one.
                </span>
              ) : (
                "Leave blank unless your authorization server requires a client secret. Stored encrypted."
              )
            }
            value={form.oauth_client_secret}
            onChange={(oauth_client_secret) => set({ oauth_client_secret })}
          />
          <Disclosure
            label="Endpoints and scopes"
            summary={endpointsMissing ? undefined : summary}
            defaultOpen={endpointsMissing}
            info="The client secret is only ever sent to this token endpoint."
          >
            <TextField
              label="Issuer"
              placeholder="https://auth.example.com"
              value={form.oauth_issuer}
              onChange={(oauth_issuer) => set({ oauth_issuer })}
            />
            <TextField
              label="Authorization endpoint"
              placeholder="https://auth.example.com/authorize"
              value={form.authorization_endpoint}
              onChange={(authorization_endpoint) =>
                set({ authorization_endpoint })
              }
            />
            <TextField
              label="Token endpoint"
              placeholder="https://auth.example.com/token"
              value={form.token_endpoint}
              onChange={(token_endpoint) => set({ token_endpoint })}
            />
            {scopes}
          </Disclosure>
        </>
      ) : (
        <Disclosure label="Scopes" summary={summary}>
          {scopes}
        </Disclosure>
      )}
    </Group>
  );
}

/**
 * Creates a connector of `owner`'s, or edits `connector`. A new connector starts from its URL, which
 * is probed for the auth the server takes before the rest of the form is shown.
 */
export function ConnectorDialog({
  owner,
  connector,
  onClose,
}: {
  owner: Owner;
  connector?: EditableConnector;
  onClose: () => void;
}) {
  const [form, setForm] = useState(() => initialForm(connector));
  // A new connector's slug follows its name until the admin types one.
  const [slugEdited, setSlugEdited] = useState(!!connector);
  const [error, setError] = useState<string>();
  const [discovery, setDiscovery] = useState<ConnectorDiscovery>();
  // A connector already serving the discovered URL, shown until the user chooses to add their own.
  const [existing, setExisting] = useState<ConnectorDiscoveryExisting>();
  const [unreachable, setUnreachable] = useState(false);
  const discover = useDiscoverConnector(owner);
  const choosingURL = !connector && !discovery;
  const options = authOptions(discovery);
  // An edit has no discovery step, so the saved server is asked again for the scopes it offers now:
  // undefined while it answers, null when it can't.
  const [savedDiscovery, setSavedDiscovery] =
    useState<ConnectorDiscovery | null>();
  const { mutateAsync: discoverSaved } = useDiscoverConnector(owner);
  const savedURL = connector?.url;
  useEffect(() => {
    if (!savedURL) return;
    let current = true;
    discoverSaved(savedURL).then(
      (found) => current && setSavedDiscovery(found),
      () => current && setSavedDiscovery(null),
    );
    return () => {
      current = false;
    };
  }, [savedURL, discoverSaved]);
  // Another URL is another server, whose scopes the saved one's answer doesn't speak for.
  const onSavedURL = !!savedURL && form.url.trim() === savedURL;
  const scopesLoading = onSavedURL && savedDiscovery === undefined;
  const scopesOffered = offeredScopes(
    discovery ?? (onSavedURL ? savedDiscovery : undefined) ?? undefined,
    connector?.oauth_scopes ?? [],
  );
  const [replacing, setReplacing] = useState<Replacing>();
  const save = useSaveConnector(owner);
  const connectorList = useConnectors();
  const orgConnectorList = useOrgConnectors({
    enabled: owner === "organization",
  });
  const connectors = connectorList.data;
  const orgConnectors = orgConnectorList.data;
  const hint = slugHint(
    owner,
    form.slug,
    connector?.id,
    connectors,
    orgConnectors,
  );
  // Discovery also waits on the connector lists, which discover.isPending doesn't cover.
  const [discovering, setDiscovering] = useState(false);
  const isPending = save.isPending || discovering;
  // The server keeps a stored secret only for the same static client on the same authorization server.
  const keepsSecret =
    !!connector?.has_oauth_client_secret &&
    connector.oauth_registration === "static" &&
    form.auth_method === "oauth" &&
    form.oauth_registration === "static" &&
    form.oauth_client_id.trim() === (connector.oauth_client_id ?? "") &&
    form.oauth_issuer.trim() === (connector.oauth_issuer ?? "") &&
    form.authorization_endpoint.trim() ===
      (connector.authorization_endpoint ?? "") &&
    form.token_endpoint.trim() === (connector.token_endpoint ?? "");
  // Saving a changed client or server without a secret leaves a public client, dropping the stored one.
  const dropsSecret =
    !!connector?.has_oauth_client_secret &&
    connector.oauth_registration === "static" &&
    form.auth_method === "oauth" &&
    form.oauth_registration === "static" &&
    !keepsSecret &&
    !form.oauth_client_secret.trim();
  const complete = isComplete(form) && !hint.error;
  // The slug sits on one line under the name until it's edited, or has to be because there's none.
  const [editingSlug, setEditingSlug] = useState(false);
  const slugOpen = editingSlug || !form.slug.trim();
  const slugNotes = (
    <>
      {hint.note && <span className="mt-1 block text-n-70">{hint.note}</span>}
      {hint.error && (
        <span className="mt-1 block text-lc-blocked">{hint.error}</span>
      )}
    </>
  );
  const slugHintText = (
    <>
      Agents reach it as{" "}
      <code className="font-mono text-xs">
        cohesive mcp {form.slug || "<slug>"}
      </code>
      .{slugNotes}
    </>
  );

  const set = (patch: Partial<Form>) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setError(undefined);
    setReplacing(undefined);
    setUnreachable(false);
  };

  const runDiscover = async () => {
    const url = form.url.trim();
    setError(undefined);
    setUnreachable(false);
    setDiscovering(true);
    try {
      // The lists may be a stale persisted cache. Refreshed alongside discovery, step 2 opens with
      // the slug hint it keeps, rather than one a later refetch rewrites under the user.
      const [found] = await Promise.all([
        discover.mutateAsync(url),
        connectorList.refetch(),
        owner === "organization" ? orgConnectorList.refetch() : undefined,
      ]);
      setForm(discoveredForm(url, found));
      // The suggested slug is free, so overriding a connector is always typed on purpose.
      setSlugEdited(true);
      setDiscovery(found);
      setExisting(found.existing_connector ?? undefined);
    } catch (discoverError) {
      const refusal = isApiError(discoverError)
        ? discoverError.data?.error
        : undefined;
      if (refusal?.code === "connector_unreachable") {
        setUnreachable(true);
        return;
      }
      setError(
        restoreErrorMessage(
          discoverError,
          "Failed to check the MCP server. Please try again.",
        ),
      );
    } finally {
      setDiscovering(false);
    }
  };

  const back = () => {
    setEditingSlug(false);
    setDiscovery(undefined);
    setExisting(undefined);
    setError(undefined);
    setReplacing(undefined);
  };

  const submit = async () => {
    const payload = toPayload(owner, form, connector);
    if (replacing) payload.replace_existing_connection = true;
    try {
      await save.mutateAsync({ connectorId: connector?.id, ...payload });
      toast.success(
        `${payload.display_name} ${connector ? "saved" : "added"}.`,
      );
      onClose();
    } catch (submitError) {
      const refusal = isApiError(submitError)
        ? submitError.data?.error
        : undefined;
      if (refusal?.code === "connection_replace_required") {
        const shared = (
          refusal as {
            connector?: {
              display_name?: string;
              connected_member_count?: number;
            };
          }
        ).connector;
        setReplacing({
          name: shared?.display_name ?? payload.slug,
          members: shared?.connected_member_count,
        });
        return;
      }
      setError(
        restoreErrorMessage(
          submitError,
          "Failed to save the connector. Please try again.",
        ),
      );
    }
  };

  const title = connector
    ? `Edit ${connector.display_name}`
    : owner === "organization"
      ? "Add connector"
      : "Add custom connector";

  return (
    <Dialog open onOpenChange={() => !isPending && onClose()}>
      <DialogContent
        className={cn(
          "gap-6 rounded-xl p-6 sm:max-w-[36rem] sm:p-8",
          NESTED_DIALOG_Z,
        )}
        overlayClassName={NESTED_DIALOG_Z}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            className="inline-flex size-12 shrink-0 items-center justify-center rounded-md border border-n-10 bg-n-05"
          >
            {/* Keyed so a corrected URL is tried again after a broken one. */}
            <IntegrationIcon
              key={form.image_url.trim()}
              imageURL={form.image_url.trim() || undefined}
              platform={form.slug}
              name={form.display_name}
              className="size-6"
            />
          </span>
          <div className="flex min-w-0 flex-col gap-1">
            <DialogTitle className="text-lg font-medium text-n-90">
              {title}
            </DialogTitle>
            <DialogDescription className="text-sm leading-relaxed text-n-60">
              {owner === "organization"
                ? "Every member of your organization can connect to it."
                : "Only you see it. A slug a shared connector already has replaces that one for you."}
            </DialogDescription>
          </div>
        </div>

        <form
          id="connector-form"
          className="flex flex-col gap-6"
          onSubmit={(event) => {
            event.preventDefault();
            if (isPending) return;
            if (choosingURL) {
              if (form.url.trim()) runDiscover();
            } else if (complete) submit();
          }}
        >
          {choosingURL && (
            <TextField
              label="MCP Server URL"
              placeholder="https://mcp.example.com/mcp"
              hint="Checked for the sign-in it supports before you set it up."
              value={form.url}
              onChange={(url) => set({ url })}
            />
          )}
          {unreachable && (
            <div
              role="alert"
              className="flex items-center justify-between gap-3 rounded-md border border-n-10 bg-n-05 p-3 text-sm text-n-80"
            >
              <span>
                Couldn't reach the server. Check the URL, or retry if it's down.
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isPending}
                isLoading={discovering}
                onClick={runDiscover}
              >
                Retry
              </Button>
            </div>
          )}

          {existing && (
            <p
              role="status"
              className="rounded-md border border-n-10 bg-n-05 p-3 text-sm text-n-80"
            >
              {existing.display_name} is already available under Connectors.
            </p>
          )}

          {!choosingURL && !existing && (
            <>
              <div className="flex flex-col gap-1.5">
                <TextField
                  label="Name"
                  placeholder="Linear"
                  value={form.display_name}
                  onChange={(display_name) =>
                    set(
                      slugEdited
                        ? { display_name }
                        : { display_name, slug: slugify(display_name) },
                    )
                  }
                />
                {discovery && (
                  <p
                    className="truncate font-mono text-xs text-n-50"
                    title={form.url}
                  >
                    {form.url}
                  </p>
                )}
                {slugOpen ? (
                  <TextField
                    label="Slug"
                    placeholder="linear"
                    hint={slugHintText}
                    value={form.slug}
                    onChange={(slug) => {
                      setSlugEdited(true);
                      setEditingSlug(true);
                      set({ slug });
                    }}
                  />
                ) : (
                  <p className="text-sm text-n-50">
                    In the CLI:{" "}
                    <code className="font-mono text-xs text-n-80">
                      {form.slug}
                    </code>{" "}
                    ·{" "}
                    <button
                      type="button"
                      aria-label="Edit slug"
                      className="cursor-pointer text-n-70 underline-offset-2 hover:text-n-90 hover:underline"
                      onClick={() => setEditingSlug(true)}
                    >
                      Edit
                    </button>
                    {slugNotes}
                  </p>
                )}
              </div>
              {!discovery && (
                <TextField
                  label="MCP Server URL"
                  placeholder="https://mcp.example.com/mcp"
                  value={form.url}
                  onChange={(url) => set({ url })}
                />
              )}

              <Group
                head="Authentication"
                info={
                  discovery
                    ? "How the server knows who's calling. Only what the server supports is offered, and Detected marks what it advertised."
                    : "How the server knows who's calling."
                }
              >
                <RadioCards
                  label="Authentication"
                  value={form.auth_method}
                  options={authChoices(owner).filter((m) =>
                    options.methods.includes(m.value),
                  )}
                  detected={discovery && discoveredAuthMethod(discovery)}
                  onChange={(auth_method) => set({ auth_method })}
                />
                {form.auth_method === "secret" && (
                  <SecretSettings form={form} set={set} />
                )}
              </Group>

              {form.auth_method === "oauth" && (
                <OAuthSettings
                  owner={owner}
                  form={form}
                  set={set}
                  hasSecret={keepsSecret}
                  dropsSecret={dropsSecret}
                  registrations={options.registrations}
                  detected={discovery && discoveredRegistration(discovery)}
                  scopesOffered={scopesOffered}
                  scopesLoading={scopesLoading}
                />
              )}

              <Disclosure label="Advanced">
                <Field label="Description" optional>
                  {(id) => (
                    <Textarea
                      id={id}
                      rows={2}
                      className="bg-input-background text-base placeholder:text-n-40"
                      value={form.description}
                      onChange={(event) =>
                        set({ description: event.target.value })
                      }
                    />
                  )}
                </Field>
                <TextField
                  label="Icon URL"
                  optional
                  placeholder="https://example.com/logo.svg"
                  value={form.image_url}
                  onChange={(image_url) => set({ image_url })}
                />
              </Disclosure>

              {/* A user's own connector has no status of theirs to set. */}
              {owner === "organization" && (
                // The form has no row inset to match.
                <div className="-mx-2.5">
                  <SettingRow
                    label={<Label htmlFor="connector-enabled">Enabled</Label>}
                    desc={
                      form.status === "disabled"
                        ? "Members can't connect, and existing connections stop working."
                        : "Members can connect."
                    }
                  >
                    <Switch
                      id="connector-enabled"
                      checked={form.status !== "disabled"}
                      onCheckedChange={(checked) =>
                        set({ status: checked ? "enabled" : "disabled" })
                      }
                    />
                  </SettingRow>
                </div>
              )}

              {replacing && (
                <p
                  role="alert"
                  className="rounded-md border border-n-10 bg-n-05 p-3 text-sm text-n-80"
                >
                  {replacingMessage(owner, replacing)}
                </p>
              )}

              <p className="text-sm text-n-50">
                {owner === "organization"
                  ? "Only add connectors from developers you trust. Cohesive doesn't control which tools a server exposes, and everyone in your organization can connect to it."
                  : "Only add connectors from developers you trust. Cohesive doesn't control which tools a server exposes or what it does with what you send it."}
              </p>
            </>
          )}

          {error && (
            <p role="alert" className="text-sm text-lc-blocked">
              {error}
            </p>
          )}
        </form>

        {existing ? (
          // Keyed apart from the form's footer: a reused button would turn into its submit
          // mid-click and submit the form.
          <div
            key="existing"
            className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end"
          >
            <Button variant="ghost" onClick={onClose}>
              Use that
            </Button>
            <Button onClick={() => setExisting(undefined)}>Add my own</Button>
          </div>
        ) : (
          <div
            key="form"
            className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end"
          >
            <Button
              variant="ghost"
              disabled={isPending}
              onClick={discovery ? back : onClose}
            >
              {discovery ? "Back" : "Cancel"}
            </Button>
            <Button
              type="submit"
              form="connector-form"
              disabled={
                (choosingURL ? !form.url.trim() : !complete) || isPending
              }
              isLoading={isPending}
            >
              {choosingURL || replacing
                ? "Continue"
                : connector
                  ? "Save"
                  : title}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
